<template lang="pug">
notification-list.notification-management(
  ref='template',
  titleNamePlaceholder='件名',
  modalTitle='お知らせ登録',
  modalTitlePlaceholder='件名',
  modalStartDatePlaceholder='yyyy/mm/dd hh:mm',
  modalEndDatePlaceholder='yyyy/mm/dd hh:mm',
  modalMessagePlaceholder='内容',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :setRegisterDataEvent='setRegisterDataEvent',
  :searchEvent='searchEvent',
  :sort='sort',
  :notificationDatas='notificationDatas',
  :deleteEvent='deleteEvent',
  :setEditDataEvent='setEditDataEvent',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  :notificationEditData='notificationEditData',
  :modalKeepButtonEvent='modalKeepButtonEvent',
  :modalErrorMessages='modalErrorMessages',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#breadcrumbButtonLabel) 新規登録
  template(#searchButtonText) 絞り込む
  template(#titleHeader) 件名
  template(#startDateHeader) 表示開始日時
  template(#endDateHeader) 終了日時
  template(#editHeader) 編集
  template(#popupRemoveText) 削除
  template(#popupEditText) 編集
  template(#modalTitleLabel) 件名
  template(#modalStartDateLabel) 表示開始日時
  template(#modalEndDateLabel) 終了日時
  template(#modalMessageLabel) 内容
  template(#modalKeepText) 保存
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import NotificationsApi from '@/mixins/api/NotificationsApi'
import NotificationList from '@/components/templates/manager/NotificationList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import {
  NotificationListType,
  NotificationListSortType,
  NotificationEditType,
  NotificationSearchParamsType,
} from '@/models/notificationModel'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { SortSettings } from '@/const/SortSettings'
import { NotificationManagementUtils } from '@/utils/NotificationManagementUtils'

/**
 * システム管理者・お知らせ編集画面
 */
@Component({
  components: {
    NotificationList,
  },
})
export default class NotificationManagement extends Mixins(LoginMethods, NotificationsApi) {
  // テンプレートへのアクセス
  @Ref() template!: NotificationList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: 'お知らせ管理',
    },
  ]

  // システム管理者権限
  private userRole: UserRoles = UserRoles.ADMIN

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_NOTIFICATION

  // ソート情報
  private sort: NotificationListSortType | null = null

  // お知らせ一覧情報
  private notificationDatas: NotificationListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // モーダルに表示するお知らせ編集情報
  private notificationEditData: NotificationEditType | null = null

  // モーダルに表示するエラーメッセージ
  private modalErrorMessages: string[] = []

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして1ページ目を検索する
    this.setDefaultSort()
    await this.search(1)
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      title: SortSettings.ASC,
      startDate: SortSettings.ASC,
      endDate: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // ソートを初期化
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: NotificationListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // お知らせ一覧情報取得API呼び出し
    const params: NotificationSearchParamsType = {
      page: page,
      title: this.template.titleName,
      titleSort: this.sort.title.code,
      startedAtSort: this.sort.startDate.code,
      endedAtSort: this.sort.endDate.code,
    }
    const apiResponse = await this.searchNotification(params)
    this.notificationDatas = NotificationManagementUtils.formatNotificationDatas(apiResponse)
    this.pagenationStatus = NotificationManagementUtils.getPagenationStatus(apiResponse)
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Admin${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'AdminLogin' })
  }

  private async setRegisterDataEvent(): Promise<void> {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    // 初期値を設定
    this.notificationEditData = await NotificationManagementUtils.buildNotificationEditData()
  }

  private async setEditDataEvent(id: number): Promise<void> {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    // 編集対象お知らせ情報取得API呼び出し
    const apiResponse = await this.getNotificationData(id)
    this.notificationEditData = await NotificationManagementUtils.buildNotificationEditData(apiResponse)
    this.$loading.complete()
  }

  // モーダルの保存ボタン押下時のイベント
  private async modalKeepButtonEvent(): Promise<void> {
    if (!this.notificationEditData) {
      return
    }
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    try {
      if (this.notificationEditData.id) {
        // notificationEditDataにIDがある場合は更新処理
        await this.updateNotificationData(this.notificationEditData)
      } else {
        // ない場合は新規登録
        await this.createNotificationData(this.notificationEditData)
      }
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー発生時はエラーメッセージを設定する
      this.modalErrorMessages = [e.response.data.message]
    }
  }

  // 削除イベント
  private async deleteEvent(id: number): Promise<void> {
    this.$loading.start()
    try {
      await this.deleteNotificationData(id)
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー時はエラーメッセージをアラート
      alert(e.response.data.message)
    }
  }
}
</script>
